import { FormGroup, Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Predicate } from "@syncfusion/ej2-data";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base";
import {
  AutoCompleteComponent,
  AutoCompleteModel,
} from "@syncfusion/ej2-react-dropdowns";
import classNames from "classnames";
import { has } from "lodash";
import React from "react";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import {
  BaseFieldProps,
  AsyncAutocompleteFieldValue,
  FieldLabeledProps,
} from "../../types/fieldTypes";
import { notInListValue } from "./AsyncAutoCompleteODataV4Adaptor";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import { useAsyncAutoCompleteField } from "./useAsyncAutoCompleteField";
import { AsyncODataFieldProps } from "../types";

export interface AsyncAutoCompleteQuerySettings {
  fieldNames: string[];
  takeValue?: number;
  predicate: Predicate;
}

export interface AsyncAutoCompleteFieldProps
  extends BaseFieldProps<AsyncAutocompleteFieldValue>,
    Partial<AsyncODataFieldProps> {
  autoCompleteSettings?: Omit<
    AutoCompleteModel,
    | "placeholder"
    | "value"
    | "disabled"
    | "select"
    | "focus"
    | "onClick"
    | "change"
    | "filtering"
    | "created"
  > &
    DefaultHtmlAttributes;
  fieldNames?: string | string[];
  /** Add value to the search input to initialize a search.
   * Will be overwritten by autoFillData settings.
   */
  searchValue?: string;
  /** Add custom data to initialize results.
   * The data its not representative of a search, but any predefined assessment, and just exists on the initial auto popup of the dropdown.
   * This data is fixed, have the same model than the odata response.
   * Overwrites searchValue settings.
   */
  autoFillData?: {}[];
  searchBy?: (searchText: string) => Predicate;
  sortBy?: string;
  /** Use querySettings to allow an initial filter of more then one predicate */
  /** ...allows correct paranthesis grouping of OR statements */
  querySettings?: AsyncAutoCompleteQuerySettings;
  /** System not allows groupby if not in list option is set up */
  hasNotInListOption?: boolean;
  groupByActiveStatus?: boolean;
  labelProps?: FieldLabeledProps;

  /** Typically onSubmit event is called when the autocomplete value changes.
   * Setting this value to true calls onSubmit every time the prop value changes, instead,
   * executing the change on direct assignments inlcuding those on initial load.
   */
  submitAllAssigments?: boolean;
  onDataSubmit?: (value: Object) => void;
}

const AsyncAutoCompleteField: React.FC<AsyncAutoCompleteFieldProps> = (
  props
) => {
  const {
    autoCompleteRef,
    ready,
    isLoading,
    setIsLoading,
    isDataLoaded,
    setIsDataLoaded,
    localValue,
    autoCompleteSettings,
    handleValueChange,
    buildDefaultQuery,
    localAutofillData,
  } = useAsyncAutoCompleteField(props);
  return (
    <FormGroup
      className={classNames(
        `${
          props.isRequired && !localValue
            ? "has-required-background"
            : "ucl-async-autocomplete-field"
        }`
      )}
    >
      {props.label && (
        <FieldLabel
          labelName={props.label}
          tooltipDescription={props.description}
          fieldProps={{ ...props, value: localValue }}
          isRequired={props.isRequired}
          {...props.labelProps}
        />
      )}
      <div className="ucl-async-autocomplete-toolbar">
        {ready && autoCompleteSettings?.dataSource && (
          <div className="ucl-async-autocomplete-input-container">
            <AutoCompleteComponent
              ref={autoCompleteRef}
              id="ucl-async-autocomplete-search-list"
              className="ucl-async-autocomplete-input"
              onChange={(e: {
                target: { value: AsyncAutocompleteFieldValue };
              }) => {
                handleValueChange(e.target.value as string);
              }}
              readonly={props.disabled || isLoading}
              placeholder={
                isLoading
                  ? "Loading..."
                  : localAutofillData
                    ? "Choose or type to search more...."
                    : "Type to search..."
              }
              actionBegin={(args) => {
                /** Initital or Filtering,  */
                if (!(localAutofillData && localAutofillData.length > 0)) {
                  if (has(args, "query")) {
                    if (!isDataLoaded) {
                      /** Load Initial Value */
                      setIsLoading(true);

                      if (props.value === notInListValue) {
                        args.query = buildDefaultQuery();
                      }
                      setIsLoading(false);
                    }
                    /** Initital */
                  } else {
                    /** Load */
                    (args as any).query =
                      props.autoCompleteSettings?.query || buildDefaultQuery();
                  }
                }
              }}
              dataBound={() => {
                setIsDataLoaded(
                  !(localAutofillData && localAutofillData.length > 0)
                );
                setIsLoading(false);
              }}
              {...autoCompleteSettings}
            />
            <Icon icon={IconNames.Search} size={14} />
          </div>
        )}
      </div>
      {props.errorMessages && (
        <FormFieldErrorContainer errorMessages={props.errorMessages} />
      )}
    </FormGroup>
  );
};

export default AsyncAutoCompleteField;
